import * as yup from 'yup';
import { CampaignFormValues } from '../types';

/**
 * One validation result consumed by fields, action gates and submit.
 *
 * `visible` separates user-facing schema errors from action-only guards. A
 * guard can disable an action without inventing a second error message beside
 * a field whose Yup rule already owns the copy.
 */
export interface CampaignIssue {
  path: string;
  code: string;
  message: string;
  blocksDraft: boolean;
  blocksPrimaryAction: boolean;
  visible: boolean;
  source: 'schema' | 'policy' | 'server';
  /** Compatibility alias for Yup-oriented callers; new code uses `code`. */
  type?: string;
}

export type CampaignValidator = (values: CampaignFormValues) => CampaignIssue[];

/** Rules an incomplete draft is intentionally allowed to break. */
const DRAFT_BYPASSABLE_SCHEMA_CODES = new Set([
  'required',
  'nullable',
  'optionality',
  'defined',
  'min',
  'recurring-settings-valid',
  'text-to-win-settings',
]);

/** Flatten Yup's error tree into the common issue model. */
export function collectSchemaIssues(error: unknown): CampaignIssue[] {
  if (!(error instanceof yup.ValidationError)) return [];
  const inner = error.inner.length ? error.inner : [error];

  return inner.map(item => {
    const path = item.path || 'root';
    const code = item.type || 'validation';
    return {
      path,
      code,
      message: item.message,
      // A draft still needs a name. Other missing required values may be
      // completed later, while malformed/order errors cannot be persisted.
      blocksDraft: path === 'name' || !DRAFT_BYPASSABLE_SCHEMA_CODES.has(code),
      blocksPrimaryAction: true,
      visible: true,
      source: 'schema',
      type: item.type,
    };
  });
}

export function validateSchema(
  schema: yup.AnyObjectSchema,
  values: CampaignFormValues,
): CampaignIssue[] {
  try {
    schema.validateSync(values, { abortEarly: false });
    return [];
  } catch (error) {
    return collectSchemaIssues(error);
  }
}

interface PolicyIssueOptions {
  path?: string;
  message?: string;
  blocksDraft?: boolean;
  blocksPrimaryAction?: boolean;
}

/** Build an action-only issue when a policy predicate fails. */
export function policyIssue(
  failed: boolean,
  code: string,
  options: PolicyIssueOptions = {},
): CampaignIssue[] {
  if (!failed) return [];
  return [{
    path: options.path ?? 'root',
    code,
    message: options.message ?? code,
    blocksDraft: options.blocksDraft ?? false,
    blocksPrimaryAction: options.blocksPrimaryAction ?? true,
    visible: false,
    source: 'policy',
  }];
}

/** Keep the issue list stable when schema and policy report the same rule. */
export function mergeIssues(...groups: CampaignIssue[][]): CampaignIssue[] {
  const seen = new Set<string>();
  return groups.flat().filter(issue => {
    const key = [issue.path, issue.code, issue.blocksDraft, issue.blocksPrimaryAction].join(':');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
